import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import { useSession } from './session';
import type { Store } from './types';

/**
 * Çalışılan mağaza (F5B-21, ADR-078): üst çubuktaki seçim ve ekranlardaki mağaza seçimi aynı değeri paylaşır;
 * işletme başına bu tarayıcıda hatırlanır. Kayıtlı mağaza artık yoksa (silinmiş, kapsam dışı) ilk mağaza seçilir.
 */
interface StoreChoice {
  stores: Store[];
  loading: boolean;
  storeId: string;
  setStoreId: (id: string) => void;
}

const Ctx = createContext<StoreChoice | null>(null);
const keyOf = (orgId: string) => `zt_admin_store:${orgId}`;

function read(key: string): string {
  try {
    return window.localStorage.getItem(key) ?? '';
  } catch {
    return '';
  }
}

function write(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Depolama kapalıysa seçim yalnızca bu sayfa açıkken geçerli kalır.
  }
}

export function StoreChoiceProvider({ children }: { children: ReactNode }) {
  const session = useSession();
  const key = keyOf(session.data?.session.activeOrganizationId ?? '');
  const stores = useQuery({ queryKey: ['stores'], queryFn: () => api.get<Store[]>('/v1/admin/stores') });
  const [chosen, setChosen] = useState(() => read(key));
  useEffect(() => setChosen(read(key)), [key]);
  const list = stores.data ?? [];
  const storeId = list.some((s) => s.id === chosen) ? chosen : (list[0]?.id ?? '');
  // Seçim yokken ilk mağaza seçili tutulur: sonradan eklenen mağaza (listede ada göre önce gelse de)
  // çalışılan mağazayı sessizce değiştirmez.
  // Liste yenilenirken (ör. işletme değişince) eski listeyle karar verilmez.
  const settled = !stores.isFetching;
  useEffect(() => {
    if (settled && storeId && storeId !== chosen) setChosen(storeId);
  }, [settled, storeId, chosen]);
  const setStoreId = useCallback(
    (id: string) => {
      setChosen(id);
      write(key, id);
    },
    [key],
  );
  const value = useMemo(
    () => ({ stores: list, loading: stores.isLoading, storeId, setStoreId }),
    [list, stores.isLoading, storeId, setStoreId],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Mağaza seçimi (tek mağaza varsa seçim gösterilmez). */
export function useStoreChoice(): StoreChoice {
  const v = useContext(Ctx);
  if (!v) throw new Error('StoreChoiceProvider eksik');
  return v;
}
